/**
 * @voltxampere/engine — Zener diode DC stamp (Sprint 23 B2)
 *
 * SPICE Zener: forward Shockley + reverse breakdown branch in a
 * single, smooth, Newton-friendly equation.
 *
 *   Id(Vd) = Is · (exp(Vd/(n·Vt)) - 1)
 *          − Ibv · (exp(-(Vd + Vz)/(nz·Vt)) - 1)
 *
 * Forward (Vd ≫ 0): birinci terim Shockley.
 * Off (Vd 0 ile -Vz arası): her iki terim küçük, leakage.
 * Breakdown (Vd ≪ -Vz): ikinci terim dominant, akım clamps Vd ≈ -Vz'a.
 *
 * Geq = dI/dV (her iki branch toplam):
 *
 *   Geq_F = (Is/(n·Vt)) · exp(Vd/(n·Vt))                    [forward]
 *   Geq_R = (Ibv/(nz·Vt)) · exp(-(Vd + Vz)/(nz·Vt))         [breakdown]
 *
 * Forward junction'da pnjlim (Sprint 7 pattern) uygulanır;
 * reverse breakdown branch'inde MAX_EXP_ARG hard-clamp güvenlik
 * ağı (Newton history reverse junction için tutmaz — Vd negatif
 * exponential argument zaten doğal olarak bounded).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
import { vtAtTemperature } from "./physical-constants.js";
import { computeVcrit, pnjlim } from "./pnjlim.js";
const MAX_EXP_ARG = 80;
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
function junctionKey(componentId) {
    return `${componentId}:vd_zener`;
}
export const zenerDCStamp = (ctx, component) => {
    const Is = getNumericParam(component, "saturationCurrent", 1e-14);
    const N = getNumericParam(component, "ideality", 1);
    const Vz = getNumericParam(component, "breakdownVoltage", 5.1);
    const Ibv = getNumericParam(component, "breakdownCurrent", 5e-3);
    const Nz = getNumericParam(component, "breakdownIdeality", 1);
    const zenerCtx = { stampType: "zener", componentId: component.id };
    if (!(Is > 0) || !Number.isFinite(Is)) {
        throw new StampParameterError(`zenerDCStamp: component "${component.id}" has non-positive saturationCurrent (got ${String(Is)})`, {
            meta: {
                ...zenerCtx,
                parameter: "saturationCurrent",
                received: Is,
                expected: "> 0 and finite",
            },
        });
    }
    if (!(N > 0) || !Number.isFinite(N)) {
        throw new StampParameterError(`zenerDCStamp: component "${component.id}" has non-positive ideality (got ${String(N)})`, { meta: { ...zenerCtx, parameter: "ideality", received: N, expected: "> 0 and finite" } });
    }
    if (!(Vz > 0) || !Number.isFinite(Vz)) {
        throw new StampParameterError(`zenerDCStamp: component "${component.id}" has non-positive breakdownVoltage (got ${String(Vz)})`, {
            meta: {
                ...zenerCtx,
                parameter: "breakdownVoltage",
                received: Vz,
                expected: "> 0 and finite",
            },
        });
    }
    if (!(Ibv > 0) || !Number.isFinite(Ibv)) {
        throw new StampParameterError(`zenerDCStamp: component "${component.id}" has non-positive breakdownCurrent (got ${String(Ibv)})`, {
            meta: {
                ...zenerCtx,
                parameter: "breakdownCurrent",
                received: Ibv,
                expected: "> 0 and finite",
            },
        });
    }
    if (!(Nz > 0) || !Number.isFinite(Nz)) {
        throw new StampParameterError(`zenerDCStamp: component "${component.id}" has non-positive breakdownIdeality (got ${String(Nz)})`, {
            meta: {
                ...zenerCtx,
                parameter: "breakdownIdeality",
                received: Nz,
                expected: "> 0 and finite",
            },
        });
    }
    // Sprint 80.Z L-AYARLAR-5 — use runtime temperature from the
    // stamp context instead of the 27 °C hardcoded constant.
    const vt = vtAtTemperature(ctx.temperature);
    const nVt = N * vt;
    const nzVt = Nz * vt;
    const anode = ctx.nodeIndex(getTerminalNetId(component, "anode"));
    const cathode = ctx.nodeIndex(getTerminalNetId(component, "cathode"));
    const vdRaw = nodeVoltage(ctx.xPrev, anode) - nodeVoltage(ctx.xPrev, cathode);
    // Forward junction limiting (Nagel pnjlim).
    const vcrit = computeVcrit(nVt, Is);
    const key = junctionKey(component.id);
    const vdPrevLimited = ctx.junctionHistory.get(key) ?? 0;
    const vdLimited = pnjlim(vdRaw, vdPrevLimited, nVt, vcrit, ctx.enableDeltaVDamping);
    ctx.junctionHistory.set(key, vdLimited);
    // Forward branch (positive arg → forward bias domains).
    const argF = Math.min(vdLimited / nVt, MAX_EXP_ARG);
    const expF = Math.exp(argF);
    const idF = Is * (expF - 1);
    const geqF = (Is / nVt) * expF;
    // Reverse breakdown branch. SPICE form'u "Ibv · exp(arg)" — `-1`
    // ofseti YOK. Off region'da (Vd > -Vz, arg << 0) expR ≈ 0 → idR ≈ 0
    // (Şefim'in beklenen davranışı). Knee'de Vd ≈ -Vz, arg ≈ 0,
    // expR ≈ 1, idR ≈ Ibv. Breakdown: Vd << -Vz, arg >> 0, expR >> 1,
    // idR çok büyük → Vd ≈ -Vz'a clamp.
    const argR = Math.min(-(vdLimited + Vz) / nzVt, MAX_EXP_ARG);
    const expR = argR > -MAX_EXP_ARG ? Math.exp(argR) : 0;
    const idR = Ibv * expR;
    const geqR = (Ibv / nzVt) * expR;
    // Total: id_F (forward Shockley) − id_R (reverse breakdown).
    // Forward Id_F pozitif (anode→cathode); breakdown Id_R'ı çıkarınca
    // breakdown akımı negatif yönde (anode'dan cathode'a değil)
    // doğru işaret kazanır.
    const id = idF - idR;
    const geq = geqF + geqR;
    const ieq = id - geq * vdLimited;
    // Symmetric four-corner conductance stamp (diode pattern).
    ctx.addY(anode, anode, geq);
    ctx.addY(cathode, cathode, geq);
    ctx.addY(anode, cathode, -geq);
    ctx.addY(cathode, anode, -geq);
    // Norton equivalent.
    if (anode >= 0)
        ctx.rhs[anode] = (ctx.rhs[anode] ?? 0) - ieq;
    if (cathode >= 0)
        ctx.rhs[cathode] = (ctx.rhs[cathode] ?? 0) + ieq;
};
/**
 * Zener iki junction'ı taşır (forward Shockley + reverse breakdown).
 * Newton step-size damping için anode + cathode junction-bearing
 * olarak işaretlenir (diode ile aynı topology).
 */
export const zenerDCStampDefinition = defineDCStamp("zener", zenerDCStamp, [], ["anode", "cathode"]);
//# sourceMappingURL=zener.js.map